/**
 * The Navigator's voice for the workspace — plain, Harry or Wes, one at a time
 * (PRD-navigator-that-does-it §3.1). Every member's Navigator answers in it.
 */

import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight, Compass, Loader2 } from 'lucide-react';
import { HarryIcon } from '../../../components/harry/HarryIcon';
import { WesIcon } from '../../../components/harry/WesIcon';
import { ConciergeHelperIcon } from '../../../components/harry/ConciergeHelperIcon';
import {
  GUIDE_AGENTS,
  GUIDE_IDS,
  type GuideId,
} from '../../../lib/guide/guideAgents';
import { useGuideAgentStore } from '../../../stores/prioritization/guideAgentStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';

function GuideIcon({ id, size = 40 }: { id: GuideId; size?: number }) {
  if (id === 'harry') {
    return (
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center bg-[var(--slate-dim)] overflow-hidden dark:bg-[var(--slate)]/30">
        <HarryIcon size={size} />
      </div>
    );
  }
  if (id === 'wes') {
    return (
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center bg-[var(--slate-dim)] overflow-hidden dark:bg-[var(--slate)]/30">
        <WesIcon size={size} />
      </div>
    );
  }
  return (
    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--bg-raised)] dark:text-[var(--text-sec)]">
      <ConciergeHelperIcon size={22} />
    </div>
  );
}

interface GuideAgentsSectionProps {
  /** When true, suppresses the section header — used when nested inside Corner Assistant. */
  inline?: boolean;
}

export function GuideAgentsSection({ inline = false }: GuideAgentsSectionProps) {
  const workspaceId = getCurrentWorkspaceId();
  const userId = useUserStore((s) => s.userId);
  const addNotification = useUIStore((s) => s.addNotification);

  const activeGuide = useGuideAgentStore((s) => s.activeGuide);
  const isLoading = useGuideAgentStore((s) => s.isLoading);
  const isSaving = useGuideAgentStore((s) => s.isSaving);
  const setActiveGuide = useGuideAgentStore((s) => s.setActiveGuide);

  const handleSelect = async (guideId: GuideId) => {
    if (!workspaceId || guideId === activeGuide || isSaving) return;
    const result = await setActiveGuide(workspaceId, guideId, userId ?? undefined);
    if (result.success) {
      addNotification({
        type: 'success',
        message: `The Navigator now answers in ${GUIDE_AGENTS[guideId].voiceName}`,
      });
    } else {
      addNotification({
        type: 'error',
        message: result.error ?? 'Could not change the voice',
      });
    }
  };

  return (
    <div className="space-y-3">
      {!inline && (
        <div>
          <p className="font-heading text-sm font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] px-1">
            Navigator voice
          </p>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] px-1 mt-0.5">
            The Navigator finds where to do what a person asks. Choose the voice it answers in, for everyone in this workspace.
            Navigator uses keyword routing; Harry and Wes do the same job with different voices.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {GUIDE_IDS.map((id) => {
          const meta = GUIDE_AGENTS[id];
          const active = activeGuide === id;

          return (
            <div
              key={id}
              className={`card flex h-full flex-col gap-3 p-4 transition-all ${
                active
                  ? 'border-[var(--slate)] ring-1 ring-indigo-200 dark:border-[var(--slate)]/60 dark:ring-indigo-800/40'
                  : 'hover:border-[var(--rule)] dark:hover:border-[var(--rule-bright)]'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 scale-90 origin-top-left">
                  <GuideIcon id={id} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] line-clamp-2">
                      {meta.name}
                    </span>
                    {meta.settingsHref ? (
                      <Link to={meta.settingsHref} className="no-underline flex-shrink-0" tabIndex={-1}>
                        <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />
                      </Link>
                    ) : null}
                  </div>
                  {!isLoading && active && (
                    <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/30 dark:text-[var(--text-ter)]">
                      <CheckCircle2 className="h-3 w-3" />
                      Active
                    </span>
                  )}
                </div>
              </div>

              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] leading-snug">
                  {meta.tagline}
                </p>
                <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] leading-relaxed line-clamp-4">
                  {meta.description}
                </p>
              </div>

              <div className="flex items-center gap-3 mt-auto pt-1 flex-wrap">
                <button
                  type="button"
                  onClick={() => void handleSelect(id)}
                  disabled={active || isSaving || isLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-40 disabled:cursor-not-allowed dark:bg-[var(--slate)] dark:hover:opacity-90"
                >
                  {isSaving && !active ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Compass className="h-3.5 w-3.5" />
                  )}
                  {active ? 'Selected' : 'Use this voice'}
                </button>
                {meta.settingsHref && (
                  <Link
                    to={meta.settingsHref}
                    className="text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-pri)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)] no-underline"
                  >
                    Settings
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
